import axios from 'axios';
import qs from 'qs';
import {observable, action, set, keys, remove, isObservableArray} from 'mobx';

import { messageList } from '../store/MessageList';
import Message from '../store/Message';
import { deleteToken, getBackendUrl, getToken } from '../utils';
import { reloadCurrentUser } from '../store/currentUser';
import { cycle } from '../store/Cycle';
import failedRequests from '../store/failedRequests';


class Model {
  static basePath = '/api';
  static verboseName = 'Model';
  static _cache = null;
  static _optionsPromise = null;
  @observable static _options = null;

  @observable isInitialized = false;
  @observable isFetched = false;
  @observable id = null;
  @observable formErrors = {};

  static async ajaxRequest(method, subPath, config, onError) {
    if (!config) {
      config = {};
    }
    config.paramsSerializer = params => (
      qs.stringify(params, {arrayFormat: 'repeat'})
    );
    if (!config.headers) {
      config.headers = {};
    }
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    try {
      return await failedRequests.wait(() => axios({
        method,
        url: this.getApiUrl(subPath),
        ...config,
      }));
    } catch (error) {
      if (error.response.status === 401 && token) {
        deleteToken();
        await reloadCurrentUser();
        return await this.ajaxRequest(method, subPath, config, onError);
      }
      if (error.response.status === 500) {
        //  messageList.push(new Message('Unknown server error. Received data:', 'error', error.response.data));
         messageList.push(new Message('Server was not able to process this request.', 'error'));
      } else if (onError) {
        onError(error);
      } else {
        let msg = '';
        let rawData = null;
        if (error.response.data.detail) {
        const str = 'You do not have permission to perform this action';
          if (error.response.data.detail.indexOf(str) > -1) {
            msg = `${str}, Contact your system administrator to get the proper authorizations.`;
          } else {
            msg = error.response.data.detail;
          }
        } else if (error.response.status === 400) {
          rawData = error.response.data;
          if ('non_field_errors' in rawData) {
            msg = rawData.non_field_errors;
            rawData = null;
          } else if (typeof rawData === 'object' && rawData !== null) {
            rawData = Object.entries(rawData).map(([field, value]) => `${field}: ${value}`).join('\n');
          }
        }
        messageList.push( new Message( msg, 'error', rawData) );
      }
      throw error;
    }
  }

  static async ajaxRequestData(method, subPath, config, onError) {
    return (await this.ajaxRequest(method, subPath, config, onError)).data;
  }

  @action static async fetchOptions() {
    if (this._optionsPromise === null) {
      this._optionsPromise = this.ajaxRequest(
        'options', '/',
        { data: cycle.params },
      );
      const response = await this._optionsPromise;
      this._options = response.data;
    }
    return this._optionsPromise;
  }

  async ajaxRequest(method, subPath, config, onError) {
    return await this.constructor.ajaxRequest(
      method, `/${this.id}${subPath}`, config, onError,
    );
  }

  async ajaxRequestData(method, subPath, config, onError) {
    return (await this.ajaxRequest(method, subPath, config, onError)).data;
  }

  static get options() {
    this.fetchOptions();
    return this._options;
  }

  static get fieldsData () {
    if (this.options === null || !('actions' in this.options) ) {
      return null;
    }
    return this.options.actions.POST;
  }

  static getApiUrl(subPath) {
    return getBackendUrl(`${this.basePath}${subPath}`);
  }

  getApiUrl(subPath) {
    return this.constructor.getApiUrl(`/${this.id}${subPath}`);
  }

  static async* getListGenerator(filters) {
    const response = await this.ajaxRequest(
      'get', '/', { params: filters },
    );
    for (const instanceData of response.data) {
      const instance = await this.createInitializedInstance(instanceData);
      instance.isFetched = true;
      yield instance;
    }
  }

  static async getList(filters) {
    const instances = [];
    for await (const instance of this.getListGenerator(filters)) {
      instances.push(instance);
    }
    return instances;
  }

  static inCache(id) {
    let cache = this._cache;
    if (cache === null) {
      cache = {};
      this._cache = cache;
    }
    return id in cache;
  }

  static getInstance(id) {
    if (this.inCache(id)) {
      return this._cache[id];
    }
    const instance = new this({ id });
    this._cache[id] = instance;
    return instance;
  }

  static getForId(id) {
    const cached = this.inCache(id);
    const instance = this.getInstance(id);
    if (!cached) {
      instance.refreshFromDb();
    }
    return instance;
  }

  static removeFromCache(id) {
    if (this.inCache(id)) {
      delete this._cache[id];
    }
  }

  static async asyncGetForId(id) {
    const instance = this.getInstance(id);
    return await instance.refreshFromDb();
  }

  /** Fetches the init data and creates an initialized instance using instanceData */
  @action static async createInitializedInstance(instanceData) {
    await this.fetchOptions();
    const instance = new this(instanceData);
    instance.isInitialized = true;
    return instance;
  }

  constructor(instanceData) {
    this.setData(instanceData);
    this.initialize();
  }

  /** Fetches static init data if necessary and initializes the instance */
  @action async initialize() {
    // Don't initialize twice
    if (this.isInitialized) {
      return;
    }
    // Fetch data only if necessary
    if (!this.constructor.fieldsData) {
      await this.constructor.fetchOptions();
    }

    this.initializeIfInitDataIsPresent();
  }

  /** Initialises the instance, if static init data is presents */
  @action initializeIfInitDataIsPresent() {
    // Don't initialize twice
    if (this.isInitialized) {
      return;
    }

    this.resetToDefaults();
  }

  @action resetToDefaults(excludedFields, force=false) {
    if (!excludedFields) {
      excludedFields = [];
    }
    const fieldsData = this.constructor.fieldsData;
    if (fieldsData) {
      const fields = {};
      for (const [name, fieldData] of Object.entries(fieldsData)) {
        if (
          ('initial' in fieldData)
          && (force || typeof this[name] === "undefined")
          && (excludedFields.indexOf(name) === -1)
        ) {
          fields[name] = fieldData.initial;
        }
      }
      Object.assign(this, fields);
      this.isInitialized = true;
    }
  }

  @action setData(instanceData) {
    Object.assign(this, instanceData);
  }

  @action refreshFromDb = async () => {
    this.setData(await this.ajaxRequestData('get', '/'));
    this.isFetched = true;
    return this;
  };

  get isLoaded() {
    return this.isInitialized && this.isFetched;
  }

  get formData() {
    const data = new FormData();
    for (let [key, value] of Object.entries(this)) {
      if (
        !(key in this.constructor.fieldsData)
        || (value === undefined)
        || (typeof value === 'function')
      ) {
        continue;
      }
      if (this.constructor.fieldsData[key].type === 'file upload') {
        if (value instanceof FileList) {
          for (const file of value) {
            data.append(key, file);
          }
        }
      } else {
        if (value === null) {
          value = '';
        } else if (isObservableArray(value)) {
          value = [...value];
        }
        if (Array.isArray(value)) {
          for (const item of value) {
            data.append(key, item);
          }
        } else {
          data.append(key, value);
        }
      }
    }
    return data;
  };

  // It is useful to invalidate options to update dynamic default values.
  @action static invalidateOptions() {
    this._optionsPromise = null;
    this._options = null;
  }

  @action async save() {
    const options = {
      headers: {'Content-Type': 'multipart/form-data'},
    };
    for (const key of keys(this.formErrors)) {
      remove(this.formErrors, key);
    }
    const onError = error => {
      for (const [key, value] of Object.entries(error.response.data)) {
        set(this.formErrors, key, value);
      }
      const nonFieldErrors = this.formErrors.non_field_errors;
      if (nonFieldErrors === undefined) {
        messageList.push(new Message(
          'Unable to save, please check errors in the form.',
          'error',
        ));
      } else {
        messageList.push(new Message(
          nonFieldErrors.join('\n'),
          'error'
        ));
      }
      throw error;
    };
    if (this.id === null) {
      const formDataToSend = this.formData;
      formDataToSend.append('format', !!this.format);
      const response = await this.constructor.ajaxRequest(
        'post', '/', { data: formDataToSend, ...options },
        onError,
      );
      messageList.push(new Message('Successfully saved!', 'success'));
      this.id = response.data.id;
      this.constructor.invalidateOptions();
      return response;
    }
    else {
      const response = await this.ajaxRequest(
        'patch', '/', { data: this.formData, ...options },
        onError,
      );
      messageList.push(new Message('Successfully saved!', 'success'));
      this.constructor.invalidateOptions();
      return response;
    }
  }

  @action async delete() {
    const response = await this.ajaxRequest(
      'DELETE', '/',
    );
    messageList.push(new Message(
      `Successfully deleted “${this.toString()}”.`,
      'success',
    ));
    this.constructor.invalidateOptions();
    return response;
  };

  toString() {
    return `${this.constructor.verboseName} ${this.id}`;
  }

  get editableFields() {
    return Object.entries(this.constructor.fieldsData).filter(([fieldName, fieldData]) => !fieldData.read_only).map(
      ([fieldName, fieldData]) => fieldName
    );
  }

  @action async copyInstance() {
    await this.constructor.fetchOptions();
    return await this.constructor.createInitializedInstance({
      ...Object.fromEntries(this.editableFields.map(fieldName => [fieldName, this[fieldName]])),
      id: null,
      isFetched: true,
    });
  }

  getValueLabel = fieldName => {
    if (!this.isInitialized) {
      return null;
    }
    const value = this[fieldName];
    const filteredChoices = this.constructor.fieldsData[fieldName].choices.filter(
      choice => choice.value === value
    );
    if (filteredChoices.length === 0) {
      return value;
    }
    return filteredChoices[0].display_name;
  }
}


export default Model;
